import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import { useBlockStore } from '@/store/blockStore';
import type { Quote } from '@/types';
import type { TiptapQuoteEditorHandle } from '@/components/editor/TiptapQuoteEditor';

export type QuoteSaveStatus = 'saved' | 'saving' | 'error' | 'dirty';

interface UseQuoteSaveStateParams {
  localQuote: Quote;
  updateQuote: (id: string, data: Partial<Quote>) => Promise<unknown>;
  tiptapEditorRef: React.MutableRefObject<TiptapQuoteEditorHandle | null>;
  pendingBlockSaves: number;
  failedBlockSaves: string[];
}

/**
 * Titel-autosave (debounce), afgeleide save-status uit pending/failed block-saves,
 * flush-keten (editor → blockStore) en visibility/pagehide flush-handlers.
 */
export function useQuoteSaveState({
  localQuote,
  updateQuote,
  tiptapEditorRef,
  pendingBlockSaves,
  failedBlockSaves,
}: UseQuoteSaveStateParams) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveState, setSaveState] = useState<QuoteSaveStatus>('saved');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const didMountRef = useRef(false);
  const pendingQuoteTimeout = useRef<number | null>(null);

  const persistQuoteTitleRef = useRef<() => Promise<boolean>>();
  persistQuoteTitleRef.current = async () => {
    setSaveState('saving');
    try {
      // Alleen de titel: de rest van localQuote kan verouderd zijn. Een klant die
      // tekent terwijl de editor openstaat zou anders bij de volgende flush
      // (PDF, publiceren, review) weer op 'sent' gezet worden.
      await updateQuote(localQuote.id, { title: localQuote.title });
      setSaveState('saved');
      setLastSavedAt(new Date());
      return true;
    } catch (error) {
      setSaveState('error');
      toast.error('Opslaan mislukt', { error });
      return false;
    }
  };

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setSaveState('dirty');
    if (pendingQuoteTimeout.current) {
      window.clearTimeout(pendingQuoteTimeout.current);
    }
    pendingQuoteTimeout.current = window.setTimeout(() => {
      void persistQuoteTitleRef.current?.();
    }, 700);
    return () => {
      if (pendingQuoteTimeout.current) {
        window.clearTimeout(pendingQuoteTimeout.current);
        pendingQuoteTimeout.current = null;
      }
    };
  }, [localQuote.title]);

  const flushEditorAndPendingUpdates = useCallback(async () => {
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      active.closest('.investment-group-view') &&
      (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)
    ) {
      active.blur();
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    tiptapEditorRef.current?.flushSync();
    const blocksSaved = await useBlockStore.getState().flushPendingBlockUpdates();

    if (pendingQuoteTimeout.current) {
      window.clearTimeout(pendingQuoteTimeout.current);
      pendingQuoteTimeout.current = null;
    }
    const quoteSaved = (await persistQuoteTitleRef.current?.()) ?? false;

    return (
      blocksSaved &&
      quoteSaved &&
      useBlockStore.getState().failedBlockSaves.length === 0
    );
  }, [tiptapEditorRef]);

  useEffect(() => {
    const flush = () => {
      tiptapEditorRef.current?.flushSync();
      void useBlockStore.getState().flushPendingBlockUpdates();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [tiptapEditorRef]);

  // Echte save-status: 'saved' pas wanneer de server alle block-persists bevestigd heeft.
  const prevPendingBlockSavesRef = useRef(0);
  const prevFailedBlockSavesRef = useRef(0);
  useEffect(() => {
    const failedCount = failedBlockSaves.length;
    if (failedCount > prevFailedBlockSavesRef.current) {
      toast.error('Wijzigingen opslaan mislukt. Probeer het opnieuw.', {
        error: useBlockStore.getState().lastBlockSaveError,
      });
    }
    const hadPending = prevPendingBlockSavesRef.current > 0;
    prevPendingBlockSavesRef.current = pendingBlockSaves;
    prevFailedBlockSavesRef.current = failedCount;

    if (pendingBlockSaves > 0) {
      setSaveState('saving');
    } else if (failedCount > 0) {
      setSaveState('error');
    } else if (hadPending) {
      setSaveState('saved');
      setLastSavedAt(new Date());
    }
  }, [pendingBlockSaves, failedBlockSaves]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const blocksSaved = await flushEditorAndPendingUpdates();
      if (blocksSaved) {
        toast.success('Document opgeslagen');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveLabel =
    saveState === 'saving'
      ? 'Opslaan...'
      : saveState === 'error'
        ? 'Fout'
        : saveState === 'dirty'
          ? 'Niet opgeslagen'
          : lastSavedAt
            ? `${lastSavedAt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`
            : 'Opgeslagen';

  return {
    isSaving,
    saveState,
    saveLabel,
    lastSavedAt,
    handleSave,
    flushEditorAndPendingUpdates,
  };
}
